import axios from 'axios'
import {AdminAlert} from '../AdminAlert/AdminAlert'
import React, {useEffect, useState} from 'react'
import {
  ChangePasswordForm,
  PasswordSecurityFields,
  validatePasswordFields,
  passwordPayload,
} from '../ChangePasswordForm/ChangePasswordForm'

const fiscalRegimeNames = {
  601: 'Régimen General de Ley Personas Morales.',
  602: 'Régimen Simplificado de Ley Personas Morales.',
  603: 'Personas Morales con Fines No Lucrativos.',
  604: 'Régimen de Pequeños Contribuyentes.',
  605: 'Sueldos y Salarios e Ingresos Asimilados a Salarios.',
  606: 'Arrendamiento.',
  607: 'Régimen de Enajenación o Adquisición de Bienes.',
  608: 'Demás ingresos.',
  609: 'Régimen de Consolidación.',
  610: 'Residentes en el Extranjero sin Establecimiento Permanente en México.',
  611: 'Ingresos por Dividendos (socios y accionistas).',
  612: 'Personas Físicas con Actividades Empresariales y Profesionales.',
  613: 'Régimen Intermedio de las Personas Físicas con Actividades Empresariales.',
  614: 'Ingresos por Intereses.',
  615: 'Ingresos por la Obtención de Premios.',
  616: 'Sin Obligaciones Fiscales.',
  617: 'PEMEX.',
  618: 'Régimen Simplificado de Ley Personas Físicas.',
  619: 'Ingresos por la Obtención de Préstamos.',
  620: 'Sociedades Cooperativas de Producción que Optan por Diferir sus Ingresos.',
  621: 'Régimen de Incorporación Fiscal.',
  622: 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras PM.',
  623: 'Régimen Opcional para Grupos de Sociedades.',
  624: 'Régimen de los Coordinados.',
  625: 'Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas.',
  626: 'Régimen Simplificado de Confianza (RESICO).',
}

const generalFields = [
  ['Régimen fiscal', 'regimenFiscal'],
  ['Nombre comercial', 'nombreComercial'],
  ['Razón social', 'razonSocial'],
  ['RFC', 'rfc'],
  ['Nombre de contacto', 'nombreContacto'],
  ['Teléfono', 'telefono'],
  ['Celular', 'celular'],
  ['Correo electrónico', 'email'],
  ['CURP', 'curp'],
  ['Calle', 'calle'],
  ['No. exterior', 'exterior'],
  ['No. interior', 'interior'],
  ['Colonia', 'colonia'],
  ['Código postal', 'cp'],
  ['Delegación/municipio', 'delegacion'],
  ['Estado', 'estado'],
  ['País', 'pais'],
]

const requiredGeneralFields = new Set([
  'regimenFiscal',
  'nombreComercial',
  'razonSocial',
  'rfc',
  'nombreContacto',
  'telefono',
  'email',
  'calle',
  'exterior',
  'colonia',
  'cp',
  'delegacion',
  'estado',
  'pais',
])

const documentFields = [
  ['Identificación oficial frente', 'identificacionFrente'],
  ['Identificación oficial vuelta', 'identificacionVuelta'],
  ['Comprobante de domicilio', 'comprobanteDomicilio'],
  ['Constancia de situación fiscal', 'constanciaSituacionFiscal'],
  ['Fotografía del inmueble 1', 'fotografiaInmueble1'],
  ['Fotografía del inmueble 2', 'fotografiaInmueble2'],
  ['Fotografía del inmueble 3', 'fotografiaInmueble3'],
  ['RFC (documento histórico)', 'rfcFile'],
]

const requiredDocumentFields = new Set([
  'identificacionFrente',
  'identificacionVuelta',
  'comprobanteDomicilio',
  'constanciaSituacionFiscal',
  'fotografiaInmueble1',
])

const emptyGeneralForm = generalFields.reduce((fields, [, field]) => ({...fields, [field]: ''}), {})
const emptyCommercialForm = {
  estatus: 'Activo',
  idVendedor: '',
  idListaPrecios: '',
  diasCredito: '',
  cascadaDescuentos: '',
  condicionado1: '',
  condicion1: '',
  condicionado2: '',
  condicion2: '',
  descuentoTotal: '',
  mostrarDescuentoTotal: false,
}

function ModalDistribuidor({
  usuario,
  vendedores = [],
  listasPrecios = [],
  onClose,
  onUpdated,
  onCreated,
  viewOnly = false,
  mode = 'edit',
}) {
  const isCreate = mode === 'create'
  const distributor = usuario && usuario[0]
  const API_URL = process.env.REACT_APP_API_URL
  const [generalForm, setGeneralForm] = useState(emptyGeneralForm)
  const [documents, setDocuments] = useState({})
  const [form, setForm] = useState(emptyCommercialForm)
  const [manualActivation, setManualActivation] = useState(false)
  const [activationToast, setActivationToast] = useState(null)
  const [saving, setSaving] = useState(false)
  const [activating, setActivating] = useState(false)
  const [passwords, setPasswords] = useState({password: '', confirmPassword: ''})

  useEffect(() => {
    setManualActivation(false)
    setActivationToast(null)
    setPasswords({password: '', confirmPassword: ''})
    setDocuments({})

    if (isCreate) {
      setGeneralForm(emptyGeneralForm)
      setForm(emptyCommercialForm)
      return
    }
    if (!distributor) return
    setForm({
      estatus: ['Por Activar', 'Activo', 'Inactivo'].includes(distributor.estatus)
        ? distributor.estatus
        : 'Por Activar',
      idVendedor: distributor.idVendedor || '',
      idListaPrecios: distributor.idListaPrecios || '',
      diasCredito: distributor.diasCredito ?? '',
      cascadaDescuentos: String(distributor.cascadaDescuentos ?? ''),
      condicionado1: distributor.condicionado1 ?? '',
      condicion1: distributor.condicion1 || '',
      condicionado2: distributor.condicionado2 ?? '',
      condicion2: distributor.condicion2 || '',
      descuentoTotal: distributor.descuentoTotal ?? '',
      mostrarDescuentoTotal: Boolean(distributor.mostrarDescuentoTotal),
    })
  }, [distributor, isCreate])

  if (!isCreate && !distributor) return null

  const updateField = (field, value) => setForm((current) => ({...current, [field]: value}))
  const updateGeneralField = (field, value) =>
    setGeneralForm((current) => ({...current, [field]: value}))
  const updateDocument = (field, file) =>
    setDocuments((current) => {
      if (file) return {...current, [field]: file}
      const nextDocuments = {...current}
      delete nextDocuments[field]
      return nextDocuments
    })
  const generalFieldValue = (field) =>
    field === 'regimenFiscal'
      ? fiscalRegimeNames[distributor[field]] || distributor[field] || ''
      : distributor[field] || ''
  const nonNegativeFields = ['condicionado1', 'condicionado2', 'descuentoTotal']
  const activationMessage = !isCreate
    ? distributor.estatus === 'Por Activar'
      ? 'El distribuidor aún debe crear su contraseña para completar la activación de su cuenta.'
      : distributor.estatus === 'Inactivo'
      ? 'La cuenta del distribuidor está inactiva. Si deseas habilitar nuevamente su acceso, cambia el estatus a Activo.'
      : 'El distribuidor ya completó el proceso de activación de su cuenta. Si deseas desactivarla, cambia el estatus a Inactivo.'
    : ''
  const activationButtonClass = 'btn btn-outline-primary distributor-activation-action'
  const hasActivationSeller = Number(form.idVendedor) > 0
  const hasActivationPriceList = Number(form.idListaPrecios) > 0
  const activationRequirementsMet = hasActivationSeller && hasActivationPriceList

  const activationRequirementsError = () => {
    if (!hasActivationSeller && !hasActivationPriceList)
      return 'Para activar al distribuidor primero debes asignar un vendedor y una lista de precios.'
    if (!hasActivationSeller) return 'Selecciona un vendedor antes de activar al distribuidor.'
    if (!hasActivationPriceList)
      return 'Selecciona una lista de precios antes de activar al distribuidor.'
    return null
  }

  const validateActivationRequirements = () => {
    const validationError = activationRequirementsError()
    if (!validationError) return true
    setActivationToast({type: 'danger', text: validationError})
    return false
  }

  const validateCommercial = (passwordRequired) => {
    if (!form.idVendedor || !form.idListaPrecios)
      return 'Selecciona un vendedor y una lista de precios.'
    if (!['Por Activar', 'Activo', 'Inactivo'].includes(form.estatus))
      return 'Selecciona un estatus válido.'
    if (
      nonNegativeFields.some(
        (field) =>
          form[field] !== '' && (Number(form[field]) < 0 || Number(form[field]) > 100 || Number.isNaN(Number(form[field])))
      )
    ) {
      return 'Los porcentajes deben ser números entre 0 y 100.'
    }
    if (String(form.diasCredito).length > 50)
      return 'Los días de crédito no pueden exceder 50 caracteres.'
    if (form.cascadaDescuentos.length > 255)
      return 'La cascada de descuentos no puede exceder 255 caracteres.'
    if (form.condicion1.length > 255 || form.condicion2.length > 255)
      return 'Las condiciones no pueden exceder 255 caracteres.'
    return validatePasswordFields(passwords.password, passwords.confirmPassword, passwordRequired)
  }

  const validateCreate = () => {
    for (const [, field] of generalFields) {
      if (requiredGeneralFields.has(field) && !generalForm[field].trim()) {
        const label = generalFields.find(([, currentField]) => currentField === field)[0]
        return `${label} es obligatorio.`
      }
    }
    if (!Object.prototype.hasOwnProperty.call(fiscalRegimeNames, generalForm.regimenFiscal))
      return 'Selecciona un régimen fiscal válido.'
    if (!/^\S+@\S+\.\S+$/.test(generalForm.email.trim()))
      return 'Ingresa un correo electrónico válido.'
    if (!/^\d+$/.test(generalForm.cp.trim())) return 'Ingresa un código postal válido.'

    for (const field of requiredDocumentFields) {
      if (!documents[field]) {
        const label = documentFields.find(([, currentField]) => currentField === field)[0]
        return `${label} es obligatorio.`
      }
    }
    for (const file of Object.values(documents)) {
      if (
        !['image/png', 'image/jpeg', 'application/pdf'].includes(file.type) ||
        file.size > 4 * 1024 * 1024
      )
        return 'Los documentos deben ser JPG, PNG o PDF de máximo 4 MB.'
    }
    return validateCommercial(true)
  }

  const saveCommercialDetails = async () => {
    if (viewOnly || isCreate) return
    setActivationToast(null)
    const validationError = validateCommercial(false)
    if (validationError) {
      setActivationToast({type: 'danger', text: validationError})
      return
    }
    setSaving(true)
    try {
      const data = new FormData()
      data.append('idUsuario', distributor.id)
      Object.entries(form).forEach(([key, value]) =>
        data.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : (value ?? ''))
      )
      Object.entries(passwordPayload(passwords.password, passwords.confirmPassword)).forEach(
        ([key, value]) => data.append(key, value)
      )
      await axios.post(`${API_URL}/actualizar-detalles-comerciales`, data)
      setPasswords({password: '', confirmPassword: ''})
      await onUpdated(distributor.id)
      setActivationToast({type: 'success', text: 'Detalles comerciales guardados correctamente.'})
    } catch (error) {
      setActivationToast({
        type: 'danger',
        text: error.response?.data?.error || 'No fue posible guardar los detalles comerciales.',
      })
    } finally {
      setSaving(false)
    }
  }

  const createDistributor = async () => {
    if (viewOnly || !isCreate) return
    setActivationToast(null)
    const validationError = validateCreate()
    if (validationError) {
      setActivationToast({type: 'danger', text: validationError})
      return
    }
    setSaving(true)
    try {
      const data = new FormData()
      Object.entries(generalForm).forEach(([key, value]) => data.append(key, value.trim()))
      Object.entries(form).forEach(([key, value]) =>
        data.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : (value ?? ''))
      )
      data.append('password', passwords.password)
      data.append('confirmPassword', passwords.confirmPassword)
      Object.entries(documents).forEach(([key, file]) => data.append(key, file))
      const {data: response} = await axios.post(`${API_URL}/distribuidores`, data)
      if (onCreated) await onCreated(response)
    } catch (error) {
      setActivationToast({
        type: 'danger',
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          'No fue posible crear el distribuidor.',
      })
    } finally {
      setSaving(false)
    }
  }

  const activateAccount = async () => {
    if (viewOnly || isCreate) return
    if (!validateActivationRequirements()) return
    setActivating(true)
    setActivationToast(null)
    try {
      const data = new FormData()
      data.append('idUsuario', distributor.id)
      data.append('idVendedor', form.idVendedor)
      await axios.post(`${API_URL}/autorizar-distribuidor`, data)
      await onUpdated(distributor.id)
      setActivationToast({type: 'success', text: 'Cuenta enviada a activación correctamente.'})
    } catch (error) {
      setActivationToast({
        type: 'danger',
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          'No fue posible habilitar la cuenta.',
      })
    } finally {
      setActivating(false)
    }
  }

  const activateManually = async () => {
    if (viewOnly || isCreate) return
    if (!validateActivationRequirements()) return
    const validationError = validatePasswordFields(passwords.password, passwords.confirmPassword)
    if (validationError) {
      setActivationToast({type: 'danger', text: validationError})
      return
    }
    setActivating(true)
    setActivationToast(null)
    try {
      await axios.put(`${API_URL}/usuarios-password-reset/${distributor.id}`, {
        newPassword: passwords.password,
        confirmPassword: passwords.confirmPassword,
        activateManually: true,
      })
      setPasswords({password: '', confirmPassword: ''})
      await onUpdated(distributor.id)
      setManualActivation(false)
      setActivationToast({type: 'success', text: 'Distribuidor activado correctamente.'})
    } catch (error) {
      setActivationToast({
        type: 'danger',
        text: error.response?.data?.message || 'No fue posible activar al distribuidor.',
      })
    } finally {
      setActivating(false)
    }
  }

  return (
    <div className='modal-distribuidor'>
      <AdminAlert message={activationToast} onClose={setActivationToast} />
      <section className='distributor-section'>
        <h3>Información general del distribuidor</h3>
        <div className='distributor-grid distributor-general-grid'>
          {isCreate
            ? generalFields.map(([label, field]) => (
                <label className='distributor-field' key={field}>
                  <span>
                    {label}
                    {requiredGeneralFields.has(field) && ' *'}
                  </span>
                  {field === 'regimenFiscal' ? (
                    <select
                      className='form-select'
                      required
                      value={generalForm[field]}
                      onChange={(event) => updateGeneralField(field, event.target.value)}
                    >
                      <option value=''>Selecciona un régimen fiscal</option>
                      {Object.entries(fiscalRegimeNames).map(([value, name]) => (
                        <option value={value} key={value}>
                          {name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className='form-control'
                      type={field === 'email' ? 'email' : 'text'}
                      maxLength={
                        ['nombreComercial', 'razonSocial', 'nombreContacto'].includes(field)
                          ? 255
                          : undefined
                      }
                      required={requiredGeneralFields.has(field)}
                      value={generalForm[field]}
                      onChange={(event) => updateGeneralField(field, event.target.value)}
                    />
                  )}
                </label>
              ))
            : (viewOnly ? [...generalFields, ['Fecha de alta', 'fechaAlta']] : generalFields).map(
                ([label, field]) => (
                  <label className='distributor-field' key={field}>
                    <span>{label}</span>
                    <input className='form-control' value={generalFieldValue(field)} readOnly />
                  </label>
                )
              )}
        </div>
      </section>

      <section className='distributor-section'>
        <h3>Documentación</h3>
        <div className='distributor-documents'>
          {isCreate
            ? documentFields.map(([label, field]) => (
                <label className='distributor-document-upload' key={field}>
                  <span>
                    {label}
                    {requiredDocumentFields.has(field) && ' *'}
                  </span>
                  <input
                    className='form-control'
                    type='file'
                    accept='.jpg,.jpeg,.png,.pdf'
                    required={requiredDocumentFields.has(field)}
                    onChange={(event) => updateDocument(field, event.target.files[0])}
                  />
                </label>
              ))
            : documentFields
                .filter(([, field]) => distributor[field] || requiredDocumentFields.has(field))
                .map(([label, field]) =>
                  distributor[field] ? (
                    <button
                      type='button'
                      className='distributor-document-button'
                      key={field}
                      onClick={() =>
                        window.open(distributor[field], '_blank', 'noopener,noreferrer')
                      }
                    >
                      <i className='fas fa-file-download' aria-hidden='true'></i>
                      <span>{label}</span>
                    </button>
                  ) : (
                    <div
                      className='distributor-document-button distributor-document-unavailable'
                      key={field}
                      aria-label={`${label}: no disponible`}
                    >
                      <i className='fas fa-file' aria-hidden='true'></i>
                      <span>
                        {label}
                        <small>No disponible en este registro</small>
                      </span>
                    </div>
                  )
                )}
        </div>
      </section>

      <section className='distributor-section'>
        <h3>Detalles comerciales</h3>
        <fieldset disabled={viewOnly} className='border-0 p-0 m-0'>
          <div className='distributor-grid distributor-commercial-grid'>
            <label className='distributor-field'>
              <span>{viewOnly ? 'Vendedor asignado' : 'Asignar vendedor'}</span>
              <select
                className='form-select'
                value={form.idVendedor ?? ''}
                onChange={(event) => updateField('idVendedor', event.target.value)}
              >
                <option value=''>{viewOnly ? 'Sin asignar' : 'Selecciona un vendedor'}</option>
                {viewOnly && form.idVendedor && (
                  <option value={form.idVendedor}>{distributor.vendedor || form.idVendedor}</option>
                )}
                {Array.isArray(vendedores) &&
                  vendedores.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.nombre} {item.apellido}
                    </option>
                  ))}
              </select>
            </label>
            <label className='distributor-field'>
              <span>Lista de precios</span>
              <select
                className='form-select'
                value={form.idListaPrecios ?? ''}
                onChange={(event) => updateField('idListaPrecios', event.target.value)}
              >
                <option value=''>{viewOnly ? 'Sin asignar' : 'Selecciona una lista'}</option>
                {viewOnly && form.idListaPrecios && (
                  <option value={form.idListaPrecios}>
                    {distributor.listaPreciosNombre || form.idListaPrecios}
                  </option>
                )}
                {listasPrecios.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.nombre}
                  </option>
                ))}
              </select>
            </label>
            <label className='distributor-field'>
              <span>Días de crédito</span>
              <input
                className='form-control'
                type='text'
                maxLength='50'
                value={form.diasCredito ?? ''}
                onChange={(event) => updateField('diasCredito', event.target.value)}
              />
            </label>
            <label className='distributor-field'>
              <span>Cascada de descuentos</span>
              <input
                className='form-control'
                type='text'
                maxLength='255'
                value={form.cascadaDescuentos ?? ''}
                onChange={(event) => updateField('cascadaDescuentos', event.target.value)}
              />
            </label>
            <label className='distributor-field'>
              <span>Descuento total (%)</span>
              <input
                className='form-control'
                type='number'
                min='0'
                step='0.01'
                value={form.descuentoTotal ?? ''}
                onChange={(event) => updateField('descuentoTotal', event.target.value)}
              />
            </label>
            <label className='distributor-field'>
              <span>Condicionado 1 (%)</span>
              <input
                className='form-control'
                type='number'
                min='0'
                step='0.01'
                value={form.condicionado1 ?? ''}
                onChange={(event) => updateField('condicionado1', event.target.value)}
              />
            </label>
            <label className='distributor-field'>
              <span>Condición 1</span>
              <input
                className='form-control'
                maxLength='255'
                value={form.condicion1 ?? ''}
                onChange={(event) => updateField('condicion1', event.target.value)}
              />
            </label>
            <label className='distributor-field'>
              <span>Condicionado 2 (%)</span>
              <input
                className='form-control'
                type='number'
                min='0'
                step='0.01'
                value={form.condicionado2 ?? ''}
                onChange={(event) => updateField('condicionado2', event.target.value)}
              />
            </label>
            <label className='distributor-field'>
              <span>Condición 2</span>
              <input
                className='form-control'
                maxLength='255'
                value={form.condicion2 ?? ''}
                onChange={(event) => updateField('condicion2', event.target.value)}
              />
            </label>
            <label className='distributor-field distributor-switch-field'>
              <span>Mostrar descuento total</span>
              <span className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={Boolean(form.mostrarDescuentoTotal)}
                  onChange={(event) => updateField('mostrarDescuentoTotal', event.target.checked)}
                />
              </span>
            </label>
          </div>
        </fieldset>
      </section>

      <section className='distributor-section distributor-account-section'>
        <div>
          <h3>
            {!isCreate && distributor.estatus === 'Por Activar'
              ? 'Activación inicial'
              : 'Estado de la cuenta'}
          </h3>
          <p>
            {isCreate
              ? 'Selecciona el estatus inicial del distribuidor.'
              : viewOnly
              ? `Estatus actual: ${distributor.estatus}.`
              : activationMessage}
          </p>
          <div className='distributor-status-actions'>
            <label className='distributor-field distributor-status'>
              <span>Estatus</span>
              {!isCreate && distributor.estatus === 'Por Activar' ? (
                <input className='form-control' value='Por activar' readOnly />
              ) : (
                <select
                  className='form-select'
                  value={form.estatus ?? ''}
                  disabled={viewOnly}
                  onChange={(event) => updateField('estatus', event.target.value)}
                >
                  {isCreate ? (
                    <>
                      <option value='Activo'>Activo</option>
                      <option value='Inactivo'>Inactivo</option>
                      <option value='Por Activar'>Por activar</option>
                    </>
                  ) : (
                    <>
                      <option value='Activo'>Activo</option>
                      <option value='Inactivo'>Inactivo</option>
                    </>
                  )}
                </select>
              )}
            </label>
            {!isCreate && !viewOnly && distributor.estatus === 'Por Activar' && (
              <button
                type='button'
                className={activationButtonClass}
                disabled={activating || saving || !activationRequirementsMet}
                title={activationRequirementsError() || undefined}
                onClick={activateAccount}
              >
                <i className='far fa-envelope me-2' aria-hidden='true'></i>
                {activating ? 'Procesando...' : 'Enviar activación'}
              </button>
            )}
          </div>
        </div>
      </section>

      <section className='distributor-section'>
        <h3>
          {isCreate
            ? 'Contraseña inicial'
            : manualActivation && distributor.estatus === 'Por Activar'
            ? 'Activar manualmente'
            : 'Contraseña y seguridad'}
        </h3>
        {isCreate ? (
          <div className='password-security-fields'>
            <PasswordSecurityFields
              newPassword={passwords.password}
              confirmPassword={passwords.confirmPassword}
              newPasswordLabel='Contraseña inicial'
              confirmPasswordLabel='Confirmar contraseña'
              onNewPasswordChange={(password) => {
                setPasswords({...passwords, password})
                setActivationToast(null)
              }}
              onConfirmPasswordChange={(confirmPassword) => {
                setPasswords({...passwords, confirmPassword})
                setActivationToast(null)
              }}
            />
          </div>
        ) : viewOnly && distributor.estatus !== 'Por Activar' ? (
          <ChangePasswordForm
            key={distributor.id}
            endpoint={`distribuidores-vendedor-password/${distributor.id}`}
            requireCurrentPassword={false}
            submitLabel='Restablecer contraseña'
            successMessage='Contraseña restablecida correctamente.'
          />
        ) : distributor.estatus === 'Por Activar' ? (
          manualActivation ? (
            <div className='password-security-fields'>
              <PasswordSecurityFields
                newPassword={passwords.password}
                confirmPassword={passwords.confirmPassword}
                confirmPasswordLabel='Confirmar contraseña'
                onNewPasswordChange={(password) => setPasswords({...passwords, password})}
                onConfirmPasswordChange={(confirmPassword) =>
                  setPasswords({...passwords, confirmPassword})
                }
              />
              <div className='d-flex gap-2 mt-4'>
                <button
                  type='button'
                  className='btn btn-primary'
                  disabled={activating || saving || !activationRequirementsMet}
                  title={activationRequirementsError() || undefined}
                  onClick={activateManually}
                >
                  {activating ? 'Activando...' : 'Confirmar activación'}
                </button>
                <button
                  type='button'
                  className='btn btn-secondary'
                  disabled={activating || saving}
                  onClick={() => {
                    setManualActivation(false)
                    setPasswords({password: '', confirmPassword: ''})
                    setActivationToast(null)
                  }}
                >
                  Cancelar activación
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className='mb-4'>
                El restablecimiento administrativo estará disponible cuando el distribuidor complete
                la activación inicial y cree su contraseña.
              </p>
              {!viewOnly && (
                <button
                  type='button'
                  className={activationButtonClass}
                  disabled={activating || saving || !activationRequirementsMet}
                  title={activationRequirementsError() || undefined}
                  onClick={() => {
                    if (!validateActivationRequirements()) return
                    setManualActivation(true)
                    setActivationToast(null)
                  }}
                >
                  <i className='fas fa-lock me-2' aria-hidden='true'></i>
                  Activar manualmente
                </button>
              )}
            </div>
          )
        ) : (
          <div className='password-security-fields'>
            <PasswordSecurityFields
              required={false}
              newPassword={passwords.password}
              confirmPassword={passwords.confirmPassword}
              onNewPasswordChange={(password) => {
                setPasswords({...passwords, password})
                setActivationToast(null)
              }}
              onConfirmPasswordChange={(confirmPassword) => {
                setPasswords({...passwords, confirmPassword})
                setActivationToast(null)
              }}
            />
          </div>
        )}
      </section>

      <div className='distributor-actions'>
        {!viewOnly && (
          <button
            type='button'
            className='btn btn-primary'
            disabled={saving || activating || manualActivation}
            onClick={isCreate ? createDistributor : saveCommercialDetails}
          >
            {saving
              ? isCreate
                ? 'Creando...'
                : 'Guardando...'
              : isCreate
              ? 'Crear distribuidor'
              : 'Guardar cambios'}
          </button>
        )}
        <button
          type='button'
          className='btn btn-secondary'
          disabled={saving || activating}
          onClick={onClose}
        >
          {viewOnly ? 'Cerrar' : 'Cancelar'}
        </button>
      </div>
    </div>
  )
}

export {ModalDistribuidor}
